import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle, CloudCheck, Info } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { AppShell } from "@/components/AppShell";
import { Spinner } from "@/components/Spinner";
import { DisclaimerNote } from "@/components/DisclaimerNote";
import { api, apiError, FRAMEWORK_LABELS } from "@/lib/api";

const OPTIONS = [
  ["yes", "Yes", "Implemented and evidenced today"],
  ["partially", "Partially", "Partly in place or inconsistent"],
  ["no", "No", "Not in place"],
  ["na", "Not applicable", "Excluded from scoring"],
  ["dont_know", "Don't Know", "Uncertain, flagged as validation risk"],
];

export default function Assessment() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const queue = useRef([]);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/assessments/${assessmentId}`);
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e));
      navigate("/dashboard");
    }
  }, [assessmentId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const flush = useCallback(async () => {
    if (queue.current.length === 0) return;
    const batch = queue.current;
    queue.current = [];
    setSaving(true);
    try {
      const { data: res } = await api.put(`/assessments/${assessmentId}/answers`, batch);
      setSavedAt(new Date());
      setData((d) => (d ? { ...d, answered: res.answered } : d));
    } catch (e) {
      // Keep unsaved answers in the queue so a transient failure cannot block submission.
      queue.current = [...batch.filter((b) => !queue.current.some((q) => q.question_code === b.question_code)),
                       ...queue.current];
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  }, [assessmentId]);

  const enqueue = (payload) => {
    queue.current = [...queue.current.filter((q) => q.question_code !== payload.question_code), payload];
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, 700);
  };

  const setAnswer = (sectionIdx, code, answer) => {
    setData((d) => {
      const sections = d.sections.map((s, i) =>
        i !== sectionIdx ? s : { ...s, questions: s.questions.map((q) => (q.code === code ? { ...q, answer } : q)) }
      );
      const withCounts = sections.map((s) => ({ ...s, answered: s.questions.filter((q) => q.answer).length }));
      const answered = withCounts.reduce((acc, s) => acc + s.answered, 0);
      return { ...d, sections: withCounts, answered };
    });
    const q = data.sections[sectionIdx].questions.find((x) => x.code === code);
    enqueue({ question_code: code, answer, comment: q?.comment || "" });
  };

  const setComment = (sectionIdx, code, comment) => {
    setData((d) => {
      const sections = d.sections.map((s, i) =>
        i !== sectionIdx ? s : { ...s, questions: s.questions.map((q) => (q.code === code ? { ...q, comment } : q)) }
      );
      return { ...d, sections };
    });
    const q = data.sections[sectionIdx].questions.find((x) => x.code === code);
    if (q?.answer) enqueue({ question_code: code, answer: q.answer, comment });
  };

  const submit = async () => {
    await flush();
    try {
      await api.post(`/assessments/${assessmentId}/submit`);
      navigate(`/results/${assessmentId}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!data)
    return (
      <AppShell breadcrumb="assessment">
        <Spinner label="Loading assessment" />
      </AppShell>
    );

  const section = data.sections[active];
  const pct = data.total ? Math.round((data.answered / data.total) * 100) : 0;
  const complete = data.answered >= data.total;

  return (
    <AppShell breadcrumb={`assessment / ${data.product.name}`}>
      <div className="grid gap-8 lg:grid-cols-[260px_1fr]">
        <aside className="lg:sticky lg:top-20 lg:self-start">
          <p className="overline text-navy-800">Sections</p>
          <div className="mt-4 border border-slate-200 bg-white" data-testid="section-nav">
            {data.sections.map((s, i) => {
              const answered = s.questions.filter((q) => q.answer).length;
              const done = s.total > 0 && answered === s.total;
              return (
                <button
                  key={s.key}
                  onClick={() => setActive(i)}
                  className={`flex w-full items-start gap-2 border-b border-slate-200 px-3.5 py-3 text-left text-xs transition-colors last:border-b-0 ${
                    i === active ? "bg-navy-900 text-white" : "text-slate-700 hover:bg-slate-50"
                  }`}
                  data-testid={`section-nav-${s.key}`}
                >
                  <span className={`mt-0.5 font-mono ${i === active ? "text-blue-200" : "text-slate-400"}`}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="flex-1">{s.name}</span>
                  <span
                    className={`font-mono ${i === active ? "text-blue-200" : done ? "text-teal-700" : "text-slate-400"}`}
                    data-testid={`section-progress-${s.key}`}
                  >
                    {answered}/{s.total}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-5 border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between text-xs text-slate-600">
              <span>Overall progress</span>
              <span className="font-mono" data-testid="overall-progress">{pct}%</span>
            </div>
            <Progress value={pct} className="mt-2 h-1.5" />
            <p className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-500" data-testid="autosave-indicator">
              <CloudCheck size={13} className={saving ? "animate-pulse text-navy-800" : "text-teal-700"} />
              {saving ? "Saving…" : savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : "Answers save automatically"}
            </p>
          </div>
        </aside>

        <div>
          <DisclaimerNote text={data.disclaimer} testId="assessment-disclaimer" />

          <div className="mt-6">
            <p className="overline text-navy-800">
              Section {active + 1} of {data.sections.length}
            </p>
            <h1 className="mt-2 font-display text-2xl font-extrabold text-navy-900 lg:text-3xl">{section.name}</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">{section.description}</p>
          </div>

          <div className="mt-7 space-y-4" data-testid="question-list">
            {section.questions.map((q) => (
              <div key={q.code} className="border border-slate-200 bg-white p-5" data-testid={`question-${q.code}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="max-w-2xl">
                    <p className="font-mono text-[11px] text-slate-400">
                      Question {q.display_number} of {data.total}
                      <span className="ml-2 text-slate-300">ref {q.code}</span>                    </p>
                    <h3 className="mt-1 font-display text-base font-bold text-navy-900">{q.text}</h3>
                    <p className="mt-2 flex gap-2 text-xs leading-relaxed text-slate-600">
                      <Info size={14} className="mt-0.5 shrink-0 text-navy-800" />
                      {q.explanation}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {q.frameworks.map((f) => (
                      <span
                        key={f}
                        className="border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-[10px] text-slate-600"
                      >
                        {FRAMEWORK_LABELS[f]}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-4 grid gap-2 sm:grid-cols-5">
                  {OPTIONS.map(([value, text, hint]) => (
                    <button
                      key={value}
                      title={hint}
                      onClick={() => setAnswer(active, q.code, value)}
                      className={`border px-3 py-2.5 text-left text-xs transition-colors ${
                        q.answer === value
                          ? "border-navy-800 bg-navy-900 text-white"
                          : "border-slate-200 bg-white text-slate-700 hover:border-navy-800 hover:text-navy-800"
                      }`}
                      data-testid={`answer-${q.code}-${value}`}
                    >
                      <span className="font-semibold">{text}</span>
                      <span className={`mt-1 block text-[10px] leading-tight ${q.answer === value ? "text-blue-100" : "text-slate-500"}`}>
                        {hint}
                      </span>
                    </button>
                  ))}
                </div>

                <Textarea
                  className="mt-3 text-sm"
                  rows={2}
                  placeholder="Optional comment: evidence location, owner, caveats"
                  value={q.comment || ""}
                  onChange={(e) => setComment(active, q.code, e.target.value)}
                  data-testid={`comment-${q.code}`}
                />
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-6">
            <Button
              variant="outline"
              disabled={active === 0}
              onClick={() => setActive(active - 1)}
              data-testid="prev-section-button"
            >
              <ArrowLeft size={15} className="mr-1.5" /> Previous
            </Button>
            <div className="flex gap-2">
              {active < data.sections.length - 1 ? (
                <Button
                  className="bg-navy-800 text-white hover:bg-navy-700"
                  onClick={async () => {
                    await flush();
                    setActive(active + 1);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  data-testid="next-section-button"
                >
                  Next section <ArrowRight size={15} className="ml-1.5" />
                </Button>
              ) : null}
              <Button
                disabled={!complete}
                onClick={submit}
                className="bg-navy-800 text-white hover:bg-navy-700 disabled:opacity-40"
                data-testid="submit-assessment-button"
              >
                <CheckCircle size={15} className="mr-1.5" />
                {complete ? "See my readiness scores" : `Answer all questions (${data.answered}/${data.total})`}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
